import { useEffect, useState } from "react";

import { fetchSeatmap } from "../api";
import { clockOf } from "../format";
import { fromSeatmap } from "../plan";
import type { Movie, Seatmap, Showtime } from "../types";
import { Dialog } from "./Dialog";
import { PlanLegend, SeatPlan } from "./SeatPlan";

type Props = {
  movie: Movie;
  showtime: Showtime;
  onClose: () => void;
};

export function PlanDialog({ movie, showtime, onClose }: Props) {
  const [seatmap, setSeatmap] = useState<Seatmap | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!showtime.cinemaId || !showtime.showingId) {
      setError("Séance sans identifiant de billetterie");
      return;
    }

    let cancelled = false;
    setSeatmap(null);
    setError(null);

    fetchSeatmap(showtime.cinemaId, showtime.showingId)
      .then((plan) => !cancelled && setSeatmap(plan))
      .catch((cause: Error) => !cancelled && setError(cause.message));

    return () => {
      cancelled = true;
    };
  }, [showtime]);

  const subtitle = `${clockOf(showtime.time)} · ${showtime.version} · Salle ${showtime.auditorium}${
    showtime.formats.length ? ` · ${showtime.formats.join(" · ")}` : ""
  }`;

  return (
    <Dialog
      title={movie.title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <span className="grow" />
          <a className="btn btn-primary" href={showtime.bookingUrl} target="_blank" rel="noreferrer">
            Réserver sur pathe.fr
          </a>
        </>
      }
    >
      {!seatmap && !error && (
        <div className="loading">
          <div className="spinner" />
          <span className="state">lecture du plan…</span>
        </div>
      )}

      {error && (
        <div className="empty">
          <p className="empty-title">Plan indisponible</p>
          <p className="empty-text">
            {error.includes("limite")
              ? error
              : "Pathé ne diffuse plus le plan d'une séance passée, complète ou fermée à la vente."}
          </p>
        </div>
      )}

      {seatmap && (
        <>
          <div className="gauge">
            <div>
              <div className="gauge-figure">{Math.round(seatmap.occupancy * 100)} %</div>
              <div className="gauge-note">
                {seatmap.free} place{seatmap.free > 1 ? "s" : ""} libre{seatmap.free > 1 ? "s" : ""} sur{" "}
                {seatmap.free + seatmap.sold}
              </div>
            </div>
            <div className="bar">
              <span className="bar-fill" style={{ width: `${seatmap.occupancy * 100}%` }} />
            </div>
          </div>

          <div style={{ marginTop: 22 }}>
            <SeatPlan plan={fromSeatmap(seatmap)} />
          </div>
          <PlanLegend />
        </>
      )}
    </Dialog>
  );
}
